import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Box, Button, Tooltip } from '@mui/material';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import { FilterList, SendOutlined, Add } from '@mui/icons-material';
import Filter from 'src/components/Filter/Filter';
import FILTER_TYPES from 'src/utils/constants/filter';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import loadPayments, {
  clearPaymentQueryOptions,
  GENERATE_AUTOMATIC_DEBIT_FORMATS,
  generateAutomaticDebitFormatsAction,
  downloadExcelAutomaticDebits,
  createAutomaticDebitAction
} from 'src/store/actions/paymentsActions';
import dayjs from 'src/utils/dayjs';
import { selectPaymentQueryOptions } from 'src/store/selectors/paymentSelectors';
import SearchBar from 'src/components/SearchBar';
import useDebounce from 'src/hooks/useDebounce';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import useStyles from '../styles';
import CreateAutomaticDebitDialog from '../CreateAutomaticDebitDialog';

const filters = [
  {
    title: 'COMMON.DATE',
    controlGroup: FILTER_TYPES.DATE_IN_RANGE,
    items: [
      {
        label: 'COMMON.START_DATE',
        value: 'startDate',
        view: ['year', 'month', 'day']
      },
      {
        label: 'COMMON.END_DATE',
        value: 'endDate',
        view: ['year', 'month', 'day']
      }
    ]
  }
];

function getFilterOptions(options) {
  let startDate = new Date();
  let endDate = new Date();

  if (options.startDate) {
    startDate = new Date(options.startDate);
  }

  if (options.endDate) {
    endDate = new Date(options.endDate);
  }

  return { dueDateRange: { startDate, endDate } };
}

const Toolbar = ({ className, ...rest }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const queryOptions = useSelector(selectPaymentQueryOptions);
  const [dueDateRange, setDueDateRange] = useState({
    startDate: momentLocalTimezone().startOf('month'),
    endDate: momentLocalTimezone().endOf('month')
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const isLoadingAutomaticDebitFormatGeneration = useSelector(
    isLoadingSelector([GENERATE_AUTOMATIC_DEBIT_FORMATS.action])
  );
  const [
    openGenerataAutomaticDebitLayoutsDialog,
    setOpenGenerataAutomaticDebitLayoutsDialog
  ] = useState(false);

  const [
    openCreateAutomaticDebitDialog,
    setOpenCreateAutomaticDebitDialog
  ] = useState(false);

  const handleClickFilter = useCallback(
    event => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleFilterChange = useCallback(
    event => {
      setDueDateRange(event);
      dispatch(
        loadPayments({
          ...getFilterOptions(event),
          page: 0
        })
      );
    },
    [setDueDateRange, queryOptions, getFilterOptions]
  );

  const debounceSearch = useDebounce(
    (value, queryOpts) =>
      dispatch(loadPayments({ ...queryOpts, page: 0, ...value })),
    400
  );
  const handleSearch = useCallback(
    e => {
      const { value } = e.target;
      setSearchValue(value);
      return debounceSearch({ clientName: value }, queryOptions);
    },
    [setSearchValue, debounceSearch, queryOptions]
  );

  const handleExcelExport = useCallback(() => {
    dispatch(downloadExcelAutomaticDebits());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      loadPayments({
        ...queryOptions
      })
    );
    return () => dispatch(clearPaymentQueryOptions());
  }, []);

  const handleOpenAutomaticDebitLayoutGenerationDialog = () => {
    setOpenGenerataAutomaticDebitLayoutsDialog(true);
  };

  const handleCloseAutomaticDebitLayoutGenerationDialog = () => {
    setOpenGenerataAutomaticDebitLayoutsDialog(false);
  };

  const handleOpenCreateAutomaticDebitDialog = () => {
    setOpenCreateAutomaticDebitDialog(true);
  };

  const handleCloseCreateAutomaticDebitDialog = () => {
    setOpenCreateAutomaticDebitDialog(false);
  };

  const generateAutomaticDebitCollectionLayout = () => {
    dispatch(
      generateAutomaticDebitFormatsAction(
        handleCloseAutomaticDebitLayoutGenerationDialog
      )
    );
  };

  const handleCreateAutomaticDebit = values => {
    const body = {
      ...values,
      amount: values.cfdi.amountIvaIncluded
    };
    dispatch(
      createAutomaticDebitAction(body, handleCloseCreateAutomaticDebitDialog)
    );
  };

  return (
    <>
      <Box className={clsx(classes.root, className)} {...rest}>
        <Breadcrumb doNotRedirectIndexList={[0]} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              sx={{ fontWeight: '400', marginRight: 1 }}
              color="primary"
              variant="h1"
            >
              {t('NAVIGATION.AUTOMATIC_DEBIT_COLLECTIONS')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <SearchBar handleSearch={handleSearch} searchValue={searchValue} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'right',
                width: '30%'
              }}
            >
              <Filter
                anchorEl={anchorEl}
                filterValues={dueDateRange}
                handleClose={handleCloseFilter}
                handleChange={handleFilterChange}
                filters={filters}
              />
              <Button
                className={clsx(classes.buttons, classes.outlined)}
                onClick={handleExcelExport}
                color="primary"
                sx={{ marginLeft: 2 }}
              >
                <FileDownloadIcon className={classes.icon} />
              </Button>
              <Button
                className={clsx(
                  classes.buttons,
                  classes.outlined,
                  classes.horizontalPadding
                )}
                color="primary"
                onClick={handleClickFilter}
                sx={{ marginLeft: 2 }}
              >
                <FilterList className={classes.icon} />
                {t('COMMON.FILTER')}
              </Button>
              <Tooltip
                title={t('COMMON.GENERATE_AUTOMATIC_DEBIT_COLECCITION_FORMAT')}
              >
                <Button
                  className={clsx(
                    classes.buttons,
                    classes.outlined,
                    classes.horizontalPadding
                  )}
                  color="primary"
                  onClick={handleOpenAutomaticDebitLayoutGenerationDialog}
                  sx={{ marginLeft: 2 }}
                >
                  <SendOutlined className={classes.icon} />
                </Button>
              </Tooltip>
              <Tooltip title={t('COMMON.CREATE_AUTOMATIC_DEBIT')}>
                <Button
                  className={clsx(classes.buttons, classes.outlined)}
                  sx={{ marginLeft: 2 }}
                  color="secondary"
                  onClick={handleOpenCreateAutomaticDebitDialog}
                >
                  <Add className={classes.icon} />
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>

      <ConfirmationDialog
        open={openGenerataAutomaticDebitLayoutsDialog}
        handleClose={handleCloseAutomaticDebitLayoutGenerationDialog}
        onSubmit={generateAutomaticDebitCollectionLayout}
        isLoading={isLoadingAutomaticDebitFormatGeneration}
        title={t(
          'AUTOMATIC_DEBIT_COLLECTION_VIEW.AUTOMATIC_DEBIT_COLLECTION_FORMAT_GENERATION.TITLE'
        )}
        description={t(
          'AUTOMATIC_DEBIT_COLLECTION_VIEW.AUTOMATIC_DEBIT_COLLECTION_FORMAT_GENERATION.DESCRIPTION'
        ).replace('{date}', dayjs.tz().format('DD [de] MMMM [de] YYYY'))}
      />

      <CreateAutomaticDebitDialog
        open={openCreateAutomaticDebitDialog}
        onClose={handleCloseCreateAutomaticDebitDialog}
        onSubmit={handleCreateAutomaticDebit}
      />
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string
};

export default Toolbar;
